import React, { useCallback, useMemo } from "react";
import { NicheListItem } from "../NicheListItem";
import { Checkbox } from "components/inputs/Checkbox/Checkbox";
import { INicheOption, IOnChangeCheckBox } from "../../popups/AddToNichePopup/types";
import { HighlightedText } from "../HighlightedText";
import { EMPTY_NICHES_MESSAGE, EMPTY_LIST_MESSAGE } from "./constants";
import { InfoMessage } from "../InfoMessage";
import { Loader } from "components/UI/Loader";
import { Button } from "components/buttons/Button";

import "./NicheSection.scss";

interface INicheSection {
  title: string;
  nicheFields: INicheOption[];
  isLoading: boolean;
  onCheckBoxChange: ({ index, available }: IOnChangeCheckBox) => void;
  onFollowNiche: (niche: INicheOption) => void;
  isSuggested?: boolean;
  text: string;
  isEmpty?: boolean;
}

export const NicheSection: React.FC<INicheSection> = ({
  title,
  nicheFields,
  onCheckBoxChange,
  onFollowNiche,
  text,
  isEmpty,
  isLoading,
  isSuggested = false
}) => {

  const message = useMemo(() => isEmpty ? EMPTY_NICHES_MESSAGE : EMPTY_LIST_MESSAGE, [isEmpty]);

  const getActionElem = useCallback((options: INicheOption) => {
    if (isSuggested) {
      return (
        <Button variant="primary" size="xs" onClick={() => onFollowNiche(options)}>Join</Button>
      );
    }

    return (
      <Checkbox
        name={options.id}
        value={options.id}
        checked={options.isChecked || !!options.added}
        onChange={() => onCheckBoxChange({ index: options.id, available: !!options.available })}
        disabled={options.added}
      />
    );
  }, [isSuggested, onCheckBoxChange]);

  const renderNiches = useMemo(() => {
    return nicheFields.map((el) => (
      <NicheListItem
        niche={{ ...el, name: <HighlightedText text={el.name} highlight={text} /> }}
        key={el.id}
        actionElement={getActionElem(el)}
      />
    ));
  }, [nicheFields, getActionElem, text]);

  const renderLoader = useMemo(() => (
    <div className="NicheLoader">
      <Loader />
    </div>
  ), []);

  return (
    <div className="NicheSection" data-testid={isSuggested ? "suggested" : "following"}>
      <h4 className="NicheSection-Title">{title}</h4>
      {isSuggested && !!nicheFields.length && (
        <InfoMessage>To share your content in these Niches, join them first.</InfoMessage>
      )}
      <div className="NicheSection-List">
        {isLoading ? renderLoader : (nicheFields.length ? renderNiches : <p className="NicheSection-NotFound">{message}</p>)}
      </div>
    </div>
  );
};
